<template>
  <v-select
    v-if="!streetSchema.$disabled"
    label-field="nameFull"
    value-field="nameFull"
    :disabled="disabled || !hasRequiredProps"
    :model="streetSchema"
    :values="streets"
    :placeholder="placeholder"
    :taggable="true"
    :ajax="true"
    @search="search"
    @input="setUlic"
  />
</template>

<script>
import axios from 'axios';

import vSelect from './vue-select.vue';
import debounceMixin from '../mixins/debounceMixin';

// @vuese
// Input na ULIC-a
// @group Components
export default {
  name: 'WUlicPicker',
  components: { vSelect },
  mixins: [debounceMixin],
  props: {
    // URL do TERYT API
    url: { type: String, required: false, default: 'https://teryt.api.witkac.pl' },
    // Schema
    model: { type: Object, required: false },
    // Schema kodu ULIC
    ulicModel: { type: Object, required: false },
    // Schema ulicy
    streetModel: { type: Object, required: false },
    // Wartość TERC-a
    terc: { type: String, required: true },
    // Wartość SIMC-a
    simc: { type: String, required: true },
    // Nazwa miasta
    city: { type: String, required: true },
    // Placeholder
    placeholder: { type: String, required: false, default: 'Wpisz nazwę ulicy' },
    // Tekst do wyświetlenia w przypadku pustej listy
    emptyListMsg: { type: String, required: false, default: '' },
    // Czy pokazywać błędy
    showError: { type: Boolean, default: true },
    // Czy nieaktywny
    disabled: { type: Boolean, default: false },
    // Czy używać wyszukiwania po każdym słowie osobno (niezależnie od kolejności)
    searchSplit: { type: Boolean, default: true },
    // Czy wyszukiwać po dowolnym słowie (minimum 1 słowo się zgadza)
    searchAny: { type: Boolean, default: false },
    // Czy ustawiać miasto jako ulicę, gdy nie ma ulic w mieście
    setCityAsStreet: { type: Boolean, default: true },
    // Czy wyświetlać listę ulic po wybraniu miasta
    streetListOnCitySelect: { type: Boolean, default: false }
  },
  data() {
    return { streets: [] };
  },
  computed: {
    ulicSchema() {
      return this.ulicModel || this.model.ulic;
    },
    streetSchema() {
      return this.streetModel || this.model.street;
    },
    hasRequiredProps() {
      return !!this.terc && !!this.simc;
    },
    searchParams() {
      return { terc: this.terc, placeSymbol: this.simc, count: 1, orderByLength: 'name', desc: false };
    }
  },
  watch: {
    simc() {
      this.loadNewProps();
    },
    terc() {
      this.loadNewProps();
    },
    'streetSchema.$model'(newValue, oldValue) {
      if (this.simc && this.terc && newValue !== oldValue) {
        this.debounce(() => this.checkSelectedStreet(), 500, 'selectedStreet')();
      }
    }
  },
  beforeMount() {
    if (!this.streetSchema.$model) {
      this.checkULICList();
    }
  },

  methods: {
    // @vuese
    // Obsługa zmiany TERC lub SIMC
    loadNewProps() {
      this.$set(this, 'streets', []);
      this.checkULICList();
    },
    // @vuese
    // Sprawdza czy lista ulic nie jest pusta
    checkULICList() {
      if (this.terc && this.simc) {
        this.checkSelectedStreet()
          .then(selectedStreetFound => {
            if (!selectedStreetFound) {
              this.clearValues();
              return this.checkIfCityHasStreets();
            }
          })
          .catch(error => this.$emit('error', 'Nie udało się pobrać listy ulic'));
      } else {
        this.clearValues();
      }
    },
    // @vuese
    // Sprawdza, czy wybrana ulica jest w wybranym mieście
    // Jeżeli jest, zapisuje jej nazwę i kod
    checkSelectedStreet() {
      if (!this.streetSchema.$value) {
        return Promise.resolve(false);
      }
      const params = this.searchParams;
      const search = this.streetSchema.$value;
      return this.requestUlicList({ ...params, search, searchSplit: false }).then(res => {
        if (res.data.count >= 1) {
          const foundUlic = res.data.list[0];
          this.setStreet(foundUlic.nameFull);
          this.setUlic(foundUlic);
          return true;
        }
        if (this.searchSplit) {
          return this.requestUlicList({ ...params, search, searchSplit: true }).then(innerResponse => {
            if (innerResponse.data.count >= 1) {
              const foundUlic = innerResponse.data.list[0];
              this.setStreet(foundUlic.nameFull);
              this.setUlic(foundUlic);
              return true;
            }
            return false;
          });
        }
        return false;
      });
    },
    // @vuese
    // Sprawdza, czy w wybranym mieście są ulice
    // Jeżeli nie ma, ustawia miasto jako ulicę
    checkIfCityHasStreets() {
      return this.requestUlicList(this.searchParams).then(res => {
        if (res.data.list.length === 0 && this.setCityAsStreet) {
          this.setStreet(this.city);
          this.setUlic(null);
        } else if (this.streetListOnCitySelect && res.data.list.length) {
          this.getStreetList();
        }
      });
    },
    // @vuese
    // Wysyła zapytanie o listę ulic
    requestUlicList(params) {
      return axios.get(`${this.url}/ulic`, { params });
    },
    // @vuese
    // Pobiera listę ulic do wybrania
    // @arg `searchText` - fragment nazwy ulicy do wyszukania
    getStreetList(search) {
      if (this.terc && this.simc) {
        this.requestUlicList({
          terc: this.terc,
          placeSymbol: this.simc,
          search,
          searchSplit: this.searchSplit,
          searchAny: this.searchAny
        })
          .then(res => (this.streets = res.data.list))
          .catch(error => this.$emit('error', 'Nie udało się pobrać listy ulic.'));
      }
    },

    // @vuese
    // Wyszukiwanie wprowadzonej nazwy w bazie
    // @arg `name` - Fragment tekstu do wyszukania
    search(searchText) {
      this.debounce(this.getStreetList, this.debounceTime)(searchText);
    },

    // @vuese
    // Zapisuje wybraną ulicę w schemie
    setStreet(street) {
      this.streetSchema.$setData(street);
      this.$emit('update');
    },

    // @vuese
    // Zapisuje wybrany kod ulic w schemie
    setUlic(ulicObj) {
      this.ulicSchema.$setData(ulicObj?.symbol || null);
      this.$emit('update');
    },

    // @vuese
    // Czyści wartości
    clearValues() {
      this.streetSchema.$setData(null);
      this.ulicSchema.$setData(null);
      this.$emit('update');
    }
  }
};
</script>
